import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

import axiosClient from "../lib/axiosClient";
import { COURSE_API_BASE } from "../utils/apiConfig";

// Energy fields the API returns on every successful call
interface EnergyData {
    energy: number;
    max_energy: number;
    next_energy_at?: string | null; // When the next point is restored
}

interface EnergyResponse extends EnergyData {
    success: boolean;
    errType?: string;
}

interface ErrorBody {
    message?: string;
    errType?: string;
    errors?: unknown;
}

export type RefetchResult = ({ success: true } & EnergyData) | { success: false; aborted?: boolean };

export type ConsumeResult =
| ({ success: true } & EnergyData)
| { success: false; errType?: string; errors?: unknown }; // e.g. errType for "not enough energy"

const RATE_LIMIT_MESSAGE = "Too many requests, please slow down";
const REFETCH_BUFFER_MS = 500; // Small buffer so the server has definitely ticked over

/**
 * Fetch and consume a user's energy.
 *
 * const { energy, maxEnergy, nextEnergyAt, loading, consuming, error, consumeEnergy, refetch } = useEnergy(userId);
 *
 * const res = await consumeEnergy(1);
 * if (!res.success) console.log(res.errType);
 */
export default function useEnergy(userId?: number | string | null) {
    const [energy, setEnergy] = useState<number | null>(null);
    const [maxEnergy, setMaxEnergy] = useState<number | null>(null);
    const [nextEnergyAt, setNextEnergyAt] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [consuming, setConsuming] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const abortRef = useRef<AbortController | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout>>();

    // Copies an API answer into state (state setters are stable, so this never changes)
    const applyEnergy = useCallback((data: EnergyData) => {
        setEnergy(data.energy);
        setMaxEnergy(data.max_energy);
        setNextEnergyAt(data.next_energy_at ?? null);
    }, []);

    const url = `${COURSE_API_BASE}/${userId}/energy`;

    // GET energy
    const refetch = useCallback(async (): Promise<RefetchResult> => {
        if (!userId) return { success: false };

        // A newer request replaces the one still running
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setLoading(true);
        setError(null);

        try {
            const { data } = await axiosClient.get<EnergyResponse>(url, {
                signal: controller.signal
            });

            if (data.success) {
                applyEnergy(data);
                return { ...data, success: true };
            }

            setError("Failed to load energy");
            return { success: false };
        } catch (err) {
            if (axios.isCancel(err)) return { success: false, aborted: true };

            if (axios.isAxiosError<ErrorBody>(err)) {
                setError(
                    err.response?.status === 429
                        ? RATE_LIMIT_MESSAGE
                        : err.response?.data?.message || err.message
                );
            } else {
                setError(err instanceof Error ? err.message : "Unknown error");
            }
            return { success: false };
        } finally {
            if (abortRef.current === controller) setLoading(false);
        }
    }, [userId, url, applyEnergy]);

    // POST consume energy
    const consumeEnergy = useCallback(
        async (amount = 1): Promise<ConsumeResult> => {
        if (!userId) return { success: false };

        setConsuming(true);
        setError(null);

        try {
            const { data } = await axiosClient.post<EnergyResponse>(url, { amount });

            if (data.success) {
                applyEnergy(data);
                return { ...data, success: true };
            }

            return { success: false, errType: data.errType };
        } catch (err) {
            if (!axios.isAxiosError<ErrorBody>(err)) {
                setError(err instanceof Error ? err.message : "Unknown error");
                return { success: false, errType: "network" };
            }

            const status = err.response?.status;
            const data = err.response?.data;

            // 400 -> { success: false, errType } (e.g. not enough energy): a normal outcome
            if (status === 400) {
                return { success: false, errType: data?.errType, errors: data?.errors };
            }

            setError(status === 429 ? RATE_LIMIT_MESSAGE : data?.message || err.message);
            return { success: false, errType: status ? "server" : "network" };
        } finally {
            setConsuming(false);
        }
    },
    [userId, url, applyEnergy]
);

    // Initial load / user change
    useEffect(() => {
        refetch();
        return () => abortRef.current?.abort();
    }, [refetch]);

    // Refetch automatically when the next energy point is due
    useEffect(() => {
        clearTimeout(timerRef.current);
        if (!nextEnergyAt || energy === null || maxEnergy === null || energy >= maxEnergy) return;

        const delay = new Date(nextEnergyAt).getTime() - Date.now();
        if (Number.isNaN(delay)) return;

        timerRef.current = setTimeout(refetch, Math.max(delay, 0) + REFETCH_BUFFER_MS);
        return () => clearTimeout(timerRef.current);
    }, [nextEnergyAt, energy, maxEnergy, refetch]);

    return {
        energy,
        maxEnergy,
        nextEnergyAt,
        loading,
        consuming,
        error,
        consumeEnergy,
        refetch
    };
}